import { useRef } from "react";
import { Button } from "antd";
import { PrinterOutlined, CloseOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import reportlogo from "assets/reportlogo.svg";

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000;
  display: flex; align-items: flex-start; justify-content: center;
  padding: 40px 0; overflow-y: auto;
`;
const ReportCard = styled.div`
  background: #fff; width: 820px; border-radius: 8px; padding: 40px 44px 32px;
  position: relative; box-shadow: 0 8px 32px rgba(0,0,0,0.18);
`;
const CloseBtn = styled.button`
  position: absolute; top: 14px; right: 16px; background: none; border: none;
  cursor: pointer; font-size: 18px; color: #888; &:hover { color: #333; }
`;
const CompanyHeader = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  padding-bottom: 20px; border-bottom: 1px solid #e0e0e0; margin-bottom: 24px;
`;
const LogoBox = styled.div`display: flex; align-items: center; gap: 14px;`;
const LogoImg = styled.div`
  width: 60px; height: 60px; border: 1px solid #ddd; border-radius: 6px;
  display: flex; align-items: center; justify-content: center; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; }
`;
const CompanyName = styled.div`font-size: 24px; font-weight: 700; color: #1A227F;`;
const CompanySubtitle = styled.div`font-size: 14px; font-weight: 600; color: #636980; text-transform: uppercase;`;
const MetaRight = styled.div`
  text-align: right; font-size: 14px; line-height: 1.8;
  span.label { font-weight: 700; color: #1A237E; }
  span.value { font-weight: 400; color: #374151; }
`;
const SectionRow = styled.div`display: flex; gap: 40px; margin-bottom: 28px;`;
const SectionBlock = styled.div`flex: 1;`;
const SectionLabel = styled.div`
  font-size: 12px; font-weight: 700; color: #636980; letter-spacing: 1px;
  text-transform: uppercase; margin-bottom: 8px;
`;
const ClientName = styled.div`font-size: 18px; font-weight: 700; color: #1F243B; margin-bottom: 4px;`;
const ClientAddress = styled.div`font-size: 14px; color: #1F243B; line-height: 1.6;`;
const ContactName = styled.div`font-size: 14px; font-weight: 600; color: #333; margin-bottom: 2px;`;
const SectionTitle = styled.div`
  font-size: 12px; font-weight: 700; color: #636980; letter-spacing: 0.8px;
  text-transform: uppercase; background: #f4f6fb; padding: 8px 14px;
  border-radius: 6px; margin-bottom: 16px; margin-top: 34px;
`;
const NoDataBox = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 20px; margin: 28px 0;
`;
const NoDataIcon = () => (
  <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 8 }}>
    <g transform="translate(0 1)" fill="none" fillRule="evenodd">
      <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"/>
      <g fillRule="nonzero" stroke="#d9d9d9">
        <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" fill="#fff"/>
        <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"/>
      </g>
    </g>
  </svg>
);
const NoDataText = styled.div`
  font-size: 14px; color: #00000040; font-weight: 400;
`;
const SummaryGrid = styled.div`
  display: flex; border: 1px solid #e8eaf0; border-radius: 8px;
  overflow: hidden; margin-bottom: 28px;
`;
const SummaryCell = styled.div`
  flex: 1; padding: 18px 20px; border-right: 1px solid #e8eaf0;
  &:last-child { border-right: none; }
`;
const SummaryCellLabel = styled.div`
  font-size: 10px; font-weight: 600; color: #757575; letter-spacing: 0.5px;
  text-transform: uppercase; margin-bottom: 6px;
`;
const SummaryCellValue = styled.div`
  font-size: 18px; font-weight: 700; color: ${({ accent }) => accent || "#636980"};
`;
const DistributionTable = styled.table`width: 100%; border-collapse: collapse; margin-bottom: 28px;`;
const Th = styled.th`
  font-size: 11px; font-weight: 700; color: #1A237E; letter-spacing: 0.5px;
  text-transform: uppercase; padding: 8px 12px;
  text-align: ${({ right }) => (right ? "right" : "left")};
  border-bottom: 2px solid #e8eaf0;
`;
const AccountRow = styled.tr`td { padding: 14px 12px 5px 12px; }`;
const AccountNameTd = styled.td`font-size: 14px; font-weight: 700; color: #1a1a2e;`;
const AccountAmountTd = styled.td`font-size: 14px; color: #555; text-align: right;`;
const AccountTotalTd = styled.td`font-size: 14px; font-weight: 700; color: #1A237E; text-align: right;`;
const MatterRow = styled.tr`td { padding: 8px 12px 8px 28px; background: #F7F7F7; }`;
const MatterNameTd = styled.td`font-size: 13px; color: #555;`;
const MatterAmountTd = styled.td`font-size: 13px; color: #555; text-align: right;`;
const Divider = styled.hr`border: none; border-top: 1px solid #e8eaf0; margin: 20px 0;`;
const FooterNote = styled.div`font-size: 11.5px; color: #888; line-height: 1.7; margin-bottom: 28px;`;
const PrintBtn = styled(Button)`
  background: #C2FAE5; border-color: #C2FAE5; color: black; border-radius: 6px;
  height: 40px; padding: 0 24px; display: flex; align-items: center; gap: 8px; margin: 0 auto;
  &:hover, &:focus { background: #C2FAE5 !important; border-color: #C2FAE5 !important; color: black !important; }
`;

const fmtCurrency = (n, currency = "USD") => {
  const num = Number(n) || 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currency} ${new Intl.NumberFormat("en-US").format(num)}`;
  }
};

/** Convert integer to Roman numeral string. */
const toRoman = (n) => {
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ["M", "CM", "D", "CD", "C", "XC", "L", "XL", "X", "IX", "V", "IV", "I"];
  let result = "";
  for (let i = 0; i < vals.length; i++) {
    while (n >= vals[i]) { result += syms[i]; n -= vals[i]; }
  }
  return result;
};

const today = () =>
  new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const AccountReport = ({ reportData, onClose }) => {
  const printRef = useRef();
  if (!reportData) return null;

  const { reportRes, clientRes } = reportData;

  const clientName =
    clientRes?.clienttype === "company"
      ? clientRes?.companyregisteredname || clientRes?.clientname
      : clientRes?.clientname || clientRes?.companyregisteredname;

  const clientAddress = [clientRes?.registeredaddress, clientRes?.city, clientRes?.country]
    .filter(Boolean).join(", ");

  const accountMap = {};
  (reportRes ?? []).forEach((row) => {
    const id = row.accountid;
    if (!accountMap[id]) {
      accountMap[id] = {
        accountid: id,
        accountname: row.accountname,
        currency: row.currency || row.accountcurrency || null,
        matters: {},
        total: 0,
        allocated: 0,
        unallocated: 0,
      };
    }
    const amt = Number(row.amount ?? 0);
    accountMap[id].total += amt;
    if (row.matterid === "MID0") {
      accountMap[id].unallocated += amt;
    } else {
      accountMap[id].allocated += amt;
    }
    if (!accountMap[id].matters[row.matterid]) {
      accountMap[id].matters[row.matterid] = { matterid: row.matterid, mattername: row.mattername, amount: 0 };
    }
    accountMap[id].matters[row.matterid].amount += amt;
  });

  const groupedAccounts = Object.values(accountMap).map((acc, idx) => ({
    ...acc,
    currency: acc.currency || "USD",
    matters: Object.values(acc.matters),
  }));

  const currencyGroups = Object.values(
    groupedAccounts.reduce((map, acc) => {
      const cur = acc.currency;
      if (!map[cur]) map[cur] = { currency: cur, accounts: [] };
      map[cur].accounts.push(acc);
      return map;
    }, {})
  );

  const handlePrint = () => {
    const win = window.open("", "_blank", "width=960,height=800");

    const sectionsHTML = currencyGroups.length === 0
      ? `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 20px;margin:28px 0;">
          <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg" style="margin-bottom:8px">
            <g transform="translate(0 1)" fill="none" fill-rule="evenodd">
              <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7"/>
              <g fill-rule="nonzero" stroke="#d9d9d9">
                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" fill="#fff"/>
                <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa"/>
              </g>
            </g>
          </svg>
          <div style="font-size:14px;color:rgba(0,0,0,0.25);font-weight:400;">No Fund Available</div>
        </div>`
      : currencyGroups.map((group, gIdx) => {
      const summaryLabel = `${toRoman(gIdx * 2 + 1)}. Consolidated Fund Summary (${group.currency})`;
      const distLabel = `${toRoman(gIdx * 2 + 2)}. Account-Level Distribution (${group.currency})`;
      const cur = group.currency;
      const gTotal = group.accounts.reduce((s, a) => s + a.total, 0);
      const gAllocated = group.accounts.reduce((s, a) => s + a.allocated, 0);
      const gUnalloc = group.accounts.reduce((s, a) => s + a.unallocated, 0);

      const rows = group.accounts.map((acc) => {
        const matters = acc.matters.filter((m) => m.matterid !== "MID0");
        return `
          <tr class="account-row">
            <td class="account-name">${acc.accountname}</td>
            <td class="account-amt">${fmtCurrency(acc.allocated, cur)}</td>
            <td class="account-amt">${fmtCurrency(acc.unallocated, cur)}</td>
            <td class="account-total">${fmtCurrency(acc.total, cur)}</td>
          </tr>
          ${matters.map((m) => `
            <tr class="matter-row">
              <td class="matter-name">${m.mattername}</td>
              <td class="matter-amt"></td><td class="matter-amt"></td>
              <td class="matter-amt">${fmtCurrency(m.amount ?? 0, cur)}</td>
            </tr>`).join("")}`;
      }).join("");

      return `
        <div class="section-title">${summaryLabel}</div>
        <div class="summary-grid">
          <div class="summary-cell"><div class="summary-cell-label">Total Fund</div><div class="summary-cell-value accent">${fmtCurrency(gTotal, cur)}</div></div>
          <div class="summary-cell"><div class="summary-cell-label">Allocated</div><div class="summary-cell-value">${fmtCurrency(gAllocated, cur)}</div></div>
          <div class="summary-cell"><div class="summary-cell-label">Unallocated</div><div class="summary-cell-value">${fmtCurrency(gUnalloc, cur)}</div></div>
        </div>
        <div class="section-title">${distLabel}</div>
        <table>
          <thead><tr>
            <th>Account Designation</th>
            <th class="right">Allocated</th>
            <th class="right">Unallocated</th>
            <th class="right">Total</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>`;
    }).join("");

    win.document.write(`
      <html><head><title>Consolidated Fund Report</title><style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:Arial,sans-serif;padding:40px 44px;color:#333;background:#fff}
        .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:1px solid #e0e0e0;margin-bottom:24px}
        .logo-box{display:flex;align-items:center;gap:14px}
        .logo-img{width:60px;height:60px;border:1px solid #ddd;border-radius:6px;display:flex;align-items:center;justify-content:center;overflow:hidden}
        .logo-img img{width:100%;height:100%;object-fit:contain}
        .company-name{font-size:24px;font-weight:700;color:#1A227F}
        .company-sub{font-size:14px;font-weight:600;color:#636980;text-transform:uppercase}
        .meta-right{text-align:right;font-size:14px;line-height:1.8}
        .meta-right .label{font-weight:700;color:#1A237E}
        .meta-right .value{font-weight:400;color:#374151}
        .section-row{display:flex;gap:40px;margin-bottom:28px}
        .section-block{flex:1}.section-block.right{text-align:right}
        .section-label{font-size:12px;font-weight:700;color:#636980;letter-spacing:1px;text-transform:uppercase;margin-bottom:8px}
        .client-name{font-size:18px;font-weight:700;color:#1F243B;margin-bottom:4px}
        .client-addr{font-size:14px;color:#1F243B;line-height:1.6}
        .contact-name{font-size:14px;font-weight:600;color:#333;margin-bottom:2px}
        .section-title{font-size:12px;font-weight:700;color:#636980;letter-spacing:.8px;text-transform:uppercase;background:#f4f6fb;padding:8px 14px;border-radius:6px;margin-bottom:16px;margin-top: 34px}
        .summary-grid{display:flex;border:1px solid #e8eaf0;border-radius:8px;overflow:hidden;margin-bottom:28px}
        .summary-cell{flex:1;padding:18px 20px;border-right:1px solid #e8eaf0}.summary-cell:last-child{border-right:none}
        .summary-cell-label{font-size:10px;font-weight:600;color:#757575;letter-spacing:.5px;text-transform:uppercase;margin-bottom:6px}
        .summary-cell-value{font-size:18px;font-weight:700;color:#636980}.summary-cell-value.accent{color:#1A237E}
        table{width:100%;border-collapse:collapse;margin-bottom:28px}
        thead th{font-size:11px;font-weight:700;color:#1A237E;letter-spacing:.5px;text-transform:uppercase;padding:8px 12px;text-align:left;border-bottom:2px solid #e8eaf0}
        thead th.right{text-align:right}
        tr.account-row td{padding:20px 12px 5px}
        td.account-name{font-size:14px;font-weight:700;color:#1a1a2e}
        td.account-amt{font-size:14px;color:#555;text-align:right}
        td.account-total{font-size:14px;font-weight:700;color:#1A237E;text-align:right}
        tr.matter-row td{padding:8px 12px 8px 28px;background:#F7F7F7}
        td.matter-name{font-size:13px;color:#555}td.matter-amt{font-size:13px;color:#555;text-align:right}
        hr{border:none;border-top:1px solid #e8eaf0;margin:20px 0}
        .footer-note{font-size:11.5px;color:#888;line-height:1.7}
        @media print{body{padding:20px 24px}}
      </style></head><body>
        <div class="header">
          <div class="logo-box">
            <div class="logo-img">${reportlogo ? `<img src="${reportlogo}" alt="logo"/>` : ""}</div>
            <div><div class="company-name">${clientName || "Synergy Pvt Ltd"}</div><div class="company-sub">Sydney, Australia</div></div>
          </div>
          <div class="meta-right">
            <span class="label">DATE:</span> <span class="value">${today()}</span><br/>
            <span class="label">CLIENT ID:</span> <span class="value">${clientRes?.bdo_id || "—"}</span>
          </div>
        </div>
        <div class="section-row">
          <div class="section-block">
            <div class="section-label">Client Information</div>
            <div class="client-name">${clientName || "—"}</div>
            ${clientAddress ? `<div class="client-addr">${clientAddress}</div>` : ""}
          </div>
          <div class="section-block right">
            <div class="section-label">Contact Person</div>
            <div class="contact-name">${clientRes?.primarycontactperson ? `${clientRes.primarycontactperson}`.trim() : "—"}</div>
          </div>
        </div>
        ${sectionsHTML}
        <hr/>
        <div class="footer-note">The above consolidated fund report is an official record of funds held in trust by ${clientName || "Synergy Pvt Ltd"} for the named client. Errors or omissions must be reported within 10 business days of the issue date.</div>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <ReportCard>
        <CloseBtn onClick={onClose}><CloseOutlined /></CloseBtn>

        <div ref={printRef}>
          <CompanyHeader>
            <LogoBox>
              <LogoImg>{reportlogo ? <img src={reportlogo} alt="logo" /> : ""}</LogoImg>
              <div>
                <CompanyName>{clientName || "Synergy Pvt Ltd"}</CompanyName>
                <CompanySubtitle>Sydney, Australia</CompanySubtitle>
              </div>
            </LogoBox>
            <MetaRight>
              <span className="label">DATE:</span> <span className="value">{today()}</span>
              <br />
              <span className="label">CLIENT ID:</span> <span className="value">{clientRes?.bdo_id || "—"}</span>
            </MetaRight>
          </CompanyHeader>

          <SectionRow>
            <SectionBlock>
              <SectionLabel>Client Information</SectionLabel>
              <ClientName>{clientName}</ClientName>
              {clientAddress && <ClientAddress>{clientAddress}</ClientAddress>}
            </SectionBlock>
            <SectionBlock style={{ textAlign: "right" }}>
              <SectionLabel>Contact Person</SectionLabel>
              <ContactName>
                {clientRes?.primarycontactperson ? `${clientRes.primarycontactperson}`.trim() : "—"}
              </ContactName>
            </SectionBlock>
          </SectionRow>

          {currencyGroups.length === 0 ? (
            <NoDataBox>
              <NoDataIcon />
              <NoDataText>No Fund Available</NoDataText>
            </NoDataBox>
          ) : currencyGroups.map((group, gIdx) => {
            const cur = group.currency;
            const summaryNum = toRoman(gIdx * 2 + 1);
            const distNum = toRoman(gIdx * 2 + 2);
            const gTotal = group.accounts.reduce((s, a) => s + a.total, 0);
            const gAllocated = group.accounts.reduce((s, a) => s + a.allocated, 0);
            const gUnalloc = group.accounts.reduce((s, a) => s + a.unallocated, 0);

            return (
              <div key={cur}>
                <SectionTitle>{summaryNum}. Consolidated Fund Summary ({cur})</SectionTitle>
                <SummaryGrid>
                  <SummaryCell>
                    <SummaryCellLabel>Total Fund</SummaryCellLabel>
                    <SummaryCellValue accent="#1A237E">{fmtCurrency(gTotal, cur)}</SummaryCellValue>
                  </SummaryCell>
                  <SummaryCell>
                    <SummaryCellLabel>Allocated</SummaryCellLabel>
                    <SummaryCellValue>{fmtCurrency(gAllocated, cur)}</SummaryCellValue>
                  </SummaryCell>
                  <SummaryCell>
                    <SummaryCellLabel>Unallocated</SummaryCellLabel>
                    <SummaryCellValue>{fmtCurrency(gUnalloc, cur)}</SummaryCellValue>
                  </SummaryCell>
                </SummaryGrid>

                <SectionTitle>{distNum}. Account-Level Distribution ({cur})</SectionTitle>
                <DistributionTable>
                  <thead>
                    <tr>
                      <Th>Account Designation</Th>
                      <Th right>Allocated</Th>
                      <Th right>Unallocated</Th>
                      <Th right>Total</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.accounts.map((acc) => (
                      <>
                        <AccountRow key={acc.accountid}>
                          <AccountNameTd>{acc.accountname}</AccountNameTd>
                          <AccountAmountTd>{fmtCurrency(acc.allocated, cur)}</AccountAmountTd>
                          <AccountAmountTd>{fmtCurrency(acc.unallocated, cur)}</AccountAmountTd>
                          <AccountTotalTd>{fmtCurrency(acc.total, cur)}</AccountTotalTd>
                        </AccountRow>
                        {acc.matters
                          .filter((m) => m.matterid !== "MID0")
                          .map((m) => (
                            <MatterRow key={m.matterid}>
                              <MatterNameTd>{m.mattername}</MatterNameTd>
                              <MatterAmountTd />
                              <MatterAmountTd />
                              <MatterAmountTd>{fmtCurrency(m.amount ?? 0, cur)}</MatterAmountTd>
                            </MatterRow>
                          ))}
                      </>
                    ))}
                  </tbody>
                </DistributionTable>
              </div>
            );
          })}

          <Divider />
          <FooterNote>
            The above consolidated fund report is an official record of funds held in trust by
            {clientName || "Synergy Pvt Ltd"} for the named client. Errors or omissions must be reported within
            10 business days of the issue date.
          </FooterNote>
        </div>

        <PrintBtn onClick={handlePrint}>
          <PrinterOutlined /> Print Report
        </PrintBtn>
      </ReportCard>
    </Overlay>
  );
};

export default AccountReport;